import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Spinner from '../../../components/Spinner/spinner';
import SummaryAmounts from '../../common/SummaryAmounts';
import { useClientTheme } from '../../../context/ThemeContext';
import { clientData } from '../../../types/interfaces';
import type { SelectableProductLine } from '../../../utils/productLines';
import ActivoFijoLibreForm from './ActivoFijoLibreForm';
import MontoAOtorgar from './MontoAOtorgar';
import type { CreditoMaximoState } from './CreditoMaximo.types';

export type RiskCreditoMaximoReadOnlyMode = 'risk-analysis' | 'monto-only';

export interface RiskCreditoMaximoReadOnlyProps {
  mode: RiskCreditoMaximoReadOnlyMode;
  loading?: boolean;
  creditoState: CreditoMaximoState | null;
  productLines: SelectableProductLine[];
  superaMaximo: boolean;
  localCurrencyCode: string;
  isUruguayEnabled: boolean;
  isQuotationsEnabled: boolean;
  availableAssets?: Array<{ code: string; name: string; saleValue?: number | string }>;
  solicitudClientData?: clientData;
  clientReason?: string;
  includeSolicitud?: boolean;
  summaryLayout?: 'inline' | 'below';
}

const noop = () => {};
const noopProductoChange = (
  _productId: number,
  _field: 'cantidad_otorgada' | 'monto_otorgado',
  _value: string
) => {};

const RiskCreditoMaximoReadOnly: React.FC<RiskCreditoMaximoReadOnlyProps> = ({
  mode,
  loading = false,
  creditoState,
  productLines,
  superaMaximo,
  localCurrencyCode,
  isUruguayEnabled,
  isQuotationsEnabled,
  availableAssets = [],
  solicitudClientData,
  clientReason,
  includeSolicitud = true,
  summaryLayout = 'inline',
}) => {
  const { clientTheme } = useClientTheme();
  const sectionVariant = clientTheme ? 'primary' : 'success';

  if (loading) {
    return (
      <Card display='flex' justifyContent='center' margin={{ top: '4', bottom: '4' }}>
        <Spinner size='md' variant='primary' />
      </Card>
    );
  }

  if (!creditoState) {
    return null;
  }

  if (mode === 'monto-only') {
    return (
      <MontoAOtorgar
        productos={creditoState.productos}
        productLines={productLines}
        superaMaximo={superaMaximo}
        onProductoChange={noopProductoChange}
        onBlur={noop}
        readOnly
        isQuotationsEnabled={isQuotationsEnabled}
        availableAssets={availableAssets}
        summaryLayout={summaryLayout}
        localCurrencyCode={localCurrencyCode}
      />
    );
  }

  return (
    <>
      {includeSolicitud && solicitudClientData && (
        <div style={{ marginBottom: '20px' }}>
          <Card variant={sectionVariant} modifier='weak' spacing={'2'}>
            <H
              type='4'
              variant='neutral-1'
              withMargin={false}
              weight='bold'
              modifier='strong'
            >
              Solicitud
            </H>
          </Card>
          {clientReason?.trim() ? (
            <Card display='flex' gap='4px'>
              <P type='2' variant='neutral' weight='bold'>
                Motivo:
              </P>
              <P type='2' variant='neutral'>
                {clientReason}
              </P>
            </Card>
          ) : null}
          <Card margin={{ top: '4' }} width='100%'>
            <div style={{ padding: '0 8px' }}>
              <SummaryAmounts
                clientData={solicitudClientData}
                isManualAmount={solicitudClientData.manual_total_amount}
                products={solicitudClientData.products}
                amounts={solicitudClientData.requested_amount}
                hideFianzaPagare
                localCurrencyCode={localCurrencyCode}
              />
            </div>
          </Card>
        </div>
      )}
      <ActivoFijoLibreForm
        activoFijo={creditoState.activo_fijo}
        revaluos={creditoState.revaluos}
        restricciones={creditoState.restricciones}
        activoFijoLibre={creditoState.activo_fijo_libre}
        creditoMaximoUnidades={creditoState.credito_maximo_unidades}
        creditoMaximoPesos={creditoState.credito_maximo_pesos}
        unidadCalculoCode={creditoState.unidad_calculo_code}
        calificacion={creditoState.calificacion}
        isRecalculating={false}
        readOnly
        hideCalificacion={!isUruguayEnabled}
        onRevaluosChange={noop}
        onRestriccionesChange={noop}
        onBlur={noop}
      />
      <MontoAOtorgar
        productos={creditoState.productos}
        productLines={productLines}
        superaMaximo={superaMaximo}
        onProductoChange={noopProductoChange}
        onBlur={noop}
        readOnly
        isQuotationsEnabled={isQuotationsEnabled}
        availableAssets={availableAssets}
        summaryLayout={summaryLayout}
        localCurrencyCode={localCurrencyCode}
      />
    </>
  );
};

export default RiskCreditoMaximoReadOnly;
